import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BarChart3, Download, ArrowUp, ArrowDown, ArrowUpDown, Search } from 'lucide-react';
import { API, getToken, MetricInfo } from './shared';

const MetricsTab = () => {
  const [metricsDateRange, setMetricsDateRange] = useState({ start: '', end: '' });
  const [listingMetrics, setListingMetrics] = useState([]);
  const [websiteMetrics, setWebsiteMetrics] = useState({ total_views: 0, unique_visitors: 0, daily: [] });
  const [funnelData, setFunnelData] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [metricsSearch, setMetricsSearch] = useState('');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedMetrics = useMemo(() => {
    let filtered = listingMetrics;
    if (metricsSearch) {
      const q = metricsSearch.toLowerCase();
      filtered = filtered.filter(m =>
        m.title?.toLowerCase().includes(q) ||
        m.city?.toLowerCase().includes(q) ||
        m.listing_type?.toLowerCase().includes(q)
      );
    }
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      let valA = a[sortField] ?? 0;
      let valB = b[sortField] ?? 0;
      if (sortField === 'created_at') {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
  }, [listingMetrics, sortField, sortDir, metricsSearch]);

  const renderSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-slate-300" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-blue-600" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-blue-600" />;
  };

  useEffect(() => {
    fetchMetrics('', '');
  }, []);

  const fetchMetrics = async (start, end) => {
    setLoadingMetrics(true);
    try {
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      const qs = params.toString();
      const config = { headers: { Authorization: `Bearer ${getToken()}` } };
      const [listingRes, websiteRes, funnelRes] = await Promise.all([
        axios.get(`${API}/admin/metrics/listings${qs ? '?' + qs : ''}`, config),
        axios.get(`${API}/admin/metrics/website${qs ? '?' + qs : ''}`, config),
        axios.get(`${API}/admin/metrics/funnel${qs ? '?' + qs : ''}`, config)
      ]);
      setListingMetrics(listingRes.data);
      setWebsiteMetrics(websiteRes.data);
      setFunnelData(funnelRes.data);
    } catch (error) { console.error('Error fetching metrics:', error); }
    finally { setLoadingMetrics(false); }
  };

  const exportMetricsCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (metricsDateRange.start) params.append('start_date', metricsDateRange.start);
      if (metricsDateRange.end) params.append('end_date', metricsDateRange.end);
      const qs = params.toString();
      const res = await axios.get(`${API}/admin/metrics/export${qs ? '?' + qs : ''}`, { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `proptick_metrics_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) { console.error('Export failed:', error); }
  };

  return (
    <div className="overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <h2 className="text-2xl sm:text-3xl font-semibold">Metrics</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">From</label>
            <input type="date" value={metricsDateRange.start} onChange={e => setMetricsDateRange({ ...metricsDateRange, start: e.target.value })} className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" data-testid="metrics-start-date" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">To</label>
            <input type="date" value={metricsDateRange.end} onChange={e => setMetricsDateRange({ ...metricsDateRange, end: e.target.value })} className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" data-testid="metrics-end-date" />
          </div>
          <button onClick={() => fetchMetrics(metricsDateRange.start, metricsDateRange.end)} className="h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all" data-testid="metrics-apply-filter">Apply</button>
          {(metricsDateRange.start || metricsDateRange.end) && (
            <button onClick={() => { setMetricsDateRange({ start: '', end: '' }); fetchMetrics('', ''); }} className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-all">Clear</button>
          )}
        </div>
      </div>

      {loadingMetrics ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Website Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="website-metrics">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <p className="text-sm text-slate-500 mb-1 flex items-center">Total Page Views <MetricInfo text="Total number of pages loaded across the entire website by all visitors." /></p>
              <p className="text-3xl font-bold text-slate-900" data-testid="total-page-views">{websiteMetrics.total_views.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <p className="text-sm text-slate-500 mb-1 flex items-center">Unique Visitors <MetricInfo text="Count of distinct visitors identified by browser session. Each unique browser/device counts once." /></p>
              <p className="text-3xl font-bold text-slate-900" data-testid="unique-visitors">{websiteMetrics.unique_visitors.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <p className="text-sm text-slate-500 mb-1 flex items-center">Avg Views / Day <MetricInfo text="Total page views divided by number of days with recorded activity." /></p>
              <p className="text-3xl font-bold text-slate-900">
                {websiteMetrics.daily.length > 0 ? Math.round(websiteMetrics.total_views / websiteMetrics.daily.length).toLocaleString() : 0}
              </p>
            </div>
          </div>

          {/* Daily Breakdown */}
          {websiteMetrics.daily.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8" data-testid="daily-breakdown">
              <h3 className="text-lg font-semibold mb-4">Daily Traffic</h3>
              <div className="overflow-x-auto">
                <div className="flex gap-1 items-end h-32 min-w-[300px]">
                  {[...websiteMetrics.daily].reverse().slice(-14).map((day, i) => {
                    const maxViews = Math.max(...websiteMetrics.daily.map(d => d.views), 1);
                    const height = Math.max(8, (day.views / maxViews) * 100);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                        <div className="absolute -top-10 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {day.views} views · {day.unique_count} unique
                        </div>
                        <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer" style={{ height: `${height}%` }} />
                        <span className="text-[10px] text-slate-400 -rotate-45 origin-left mt-1">{day.date?.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* User Journey Funnels */}
          {funnelData && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-testid="funnel-section">
              {/* Browse Funnel */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold mb-1">Buyer Journey</h3>
                <p className="text-xs text-slate-400 mb-5">Where visitors drop off in the buying flow</p>
                <FunnelChart steps={funnelData.browse_funnel} color="blue" />
              </div>
              {/* Seller Funnel */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold mb-1">Seller Journey</h3>
                <p className="text-xs text-slate-400 mb-5">Where visitors drop off in the listing flow</p>
                <FunnelChart steps={funnelData.seller_funnel} color="emerald" />
              </div>
            </div>
          )}

          {/* Page-Level Traffic */}
          {funnelData?.page_dropoff?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8" data-testid="page-dropoff">
              <h3 className="text-lg font-semibold mb-1">Traffic by Page</h3>
              <p className="text-xs text-slate-400 mb-5">Unique visitors per page — shows which pages retain attention</p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase py-2 pr-4">#</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase py-2 pr-4">Page</th>
                      <th className="text-right text-xs font-semibold text-slate-500 uppercase py-2 pr-4">Visitors</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase py-2 w-1/3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnelData.page_dropoff.map((p, i) => {
                      const max = funnelData.page_dropoff[0]?.unique_visitors || 1;
                      const pct = Math.round((p.unique_visitors / max) * 100);
                      const pageName = p.page_label || (p.page === '/' ? 'Homepage' : p.page.replace(/^\//, '').replace(/-/g, ' ').replace(/\//g, ' / '));
                      const isProperty = p.page?.startsWith('/property/');
                      return (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="py-2.5 pr-4 text-sm text-slate-400">{i + 1}</td>
                          <td className="py-2.5 pr-4">
                            <span className={`text-sm font-medium ${isProperty ? 'text-blue-700' : 'text-slate-800'} capitalize`}>{pageName}</span>
                            {isProperty && !p.page_label && <span className="text-[10px] text-slate-400 block">Property Page</span>}
                          </td>
                          <td className="py-2.5 pr-4 text-right">
                            <span className="text-sm font-semibold text-slate-900">{p.unique_visitors}</span>
                          </td>
                          <td className="py-2.5">
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Device / OS / Browser / Location Breakdown */}
          {(websiteMetrics.devices?.length > 0 || websiteMetrics.os?.length > 0 || websiteMetrics.browsers?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="device-breakdown">
              <BreakdownCard title="Device Type" tooltip="Desktop, Mobile, Tablet, or Bot — detected from the visitor's browser." data={websiteMetrics.devices} total={websiteMetrics.total_views} color="bg-blue-500" />
              <BreakdownCard title="Operating System" tooltip="OS detected from the visitor's device — e.g., iOS, Android, Windows, Mac OS X." data={(websiteMetrics.os || []).slice(0, 6)} total={websiteMetrics.total_views} color="bg-emerald-500" />
              <BreakdownCard title="Browser" tooltip="Browser used by visitors — e.g., Chrome, Safari, Firefox, Edge." data={(websiteMetrics.browsers || []).slice(0, 6)} total={websiteMetrics.total_views} color="bg-amber-500" />
              {/* Visitor Locations */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">Visitor Location <MetricInfo text="Country and city of visitors, detected from IP address." /></h4>
                <div className="space-y-2.5">
                  {(websiteMetrics.locations || []).filter(l => l.country !== 'Unknown').slice(0, 8).map((l, i) => {
                    const total = websiteMetrics.total_views || 1;
                    const pct = Math.round((l.count / total) * 100);
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{l.city}, {l.country}</span>
                          <span className="text-slate-500">{l.count} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-violet-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  {(!websiteMetrics.locations || websiteMetrics.locations.length === 0) && (
                    <p className="text-sm text-slate-400">No geo data yet. New visits will show location info.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Listing Metrics Table */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Per-Listing Metrics</h3>
            <button onClick={exportMetricsCSV} className="flex items-center gap-2 h-9 px-4 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-all" data-testid="export-metrics-csv">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>

          {listingMetrics.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Metrics Yet</h3>
              <p className="text-slate-500">Metrics will appear as users interact with listings</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200" data-testid="listing-metrics-table">
              {/* Search + Sort by Date */}
              <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search listings by name, city, type..."
                    value={metricsSearch}
                    onChange={(e) => setMetricsSearch(e.target.value)}
                    className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    data-testid="metrics-search"
                  />
                </div>
                <button
                  onClick={() => handleSort('created_at')}
                  className={`h-10 px-4 rounded-lg border text-sm font-medium flex items-center gap-1.5 transition-all ${sortField === 'created_at' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  data-testid="sort-date-added"
                >
                  Date Added {renderSortIcon('created_at')}
                </button>
                <span className="text-sm text-slate-400">{sortedMetrics.length} listings</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-6 py-4 font-semibold text-slate-700">Listing</th>
                      <th className="text-center px-4 py-4 font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleSort('impressions')} data-testid="sort-impressions"><span className="inline-flex items-center justify-center">Impressions <MetricInfo text="Number of times this listing appeared in browse/search results." position="bottom" />{renderSortIcon('impressions')}</span></th>
                      <th className="text-center px-4 py-4 font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleSort('clicks')} data-testid="sort-clicks"><span className="inline-flex items-center justify-center">Clicks <MetricInfo text="Number of times users clicked to open this listing's detail page." position="bottom" />{renderSortIcon('clicks')}</span></th>
                      <th className="text-center px-4 py-4 font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleSort('whatsapp_clicks')} data-testid="sort-whatsapp"><span className="inline-flex items-center justify-center">WhatsApp <MetricInfo text="Number of times the WhatsApp contact button was clicked for this listing." position="bottom" />{renderSortIcon('whatsapp_clicks')}</span></th>
                      <th className="text-center px-4 py-4 font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleSort('email_enquiries')} data-testid="sort-emails"><span className="inline-flex items-center justify-center">Emails <MetricInfo text="Number of email enquiries sent for this listing." position="bottom" />{renderSortIcon('email_enquiries')}</span></th>
                      <th className="text-center px-4 py-4 font-semibold text-slate-700 cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleSort('cvr')} data-testid="sort-cvr"><span className="inline-flex items-center justify-center">CVR % <MetricInfo text="Click-through rate: (Clicks / Impressions) x 100. Measures how often viewers click to see the full listing." position="bottom" />{renderSortIcon('cvr')}</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedMetrics.map(m => (
                      <tr key={m.listing_id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-900 truncate max-w-xs">{m.title}</p>
                          <p className="text-xs text-slate-400">{m.city} · {m.listing_type}</p>
                        </td>
                        <td className="px-4 py-4 text-center"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">{m.impressions.toLocaleString()}</span></td>
                        <td className="px-4 py-4 text-center"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">{m.clicks.toLocaleString()}</span></td>
                        <td className="px-4 py-4 text-center"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-50 text-green-700 text-sm font-medium">{m.whatsapp_clicks}</span></td>
                        <td className="px-4 py-4 text-center"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-orange-50 text-orange-700 text-sm font-medium">{m.email_enquiries}</span></td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${m.cvr >= 10 ? 'bg-green-50 text-green-700' : m.cvr >= 5 ? 'bg-yellow-50 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{m.cvr}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const FunnelChart = ({ steps, color = 'blue' }) => {
  const maxVisitors = Math.max(...steps.map(s => s.visitors), 1);
  const colors = {
    blue: { bar: 'bg-blue-500', drop: 'text-red-500', bg: 'bg-blue-50', text: 'text-blue-700' },
    emerald: { bar: 'bg-emerald-500', drop: 'text-red-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  };
  const c = colors[color] || colors.blue;

  return (
    <div className="space-y-3">
      {steps.map((step, i) => {
        const widthPct = Math.max(8, Math.round((step.visitors / maxVisitors) * 100));
        const prev = i > 0 ? steps[i - 1].visitors : null;
        const dropoff = prev && prev > 0 ? Math.round(((prev - step.visitors) / prev) * 100) : null;
        return (
          <div key={step.step}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-slate-700">{step.step}</span>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${c.text}`}>{step.visitors.toLocaleString()}</span>
                {dropoff !== null && dropoff > 0 && (
                  <span className={`text-xs font-semibold ${c.drop} ${c.bg} px-1.5 py-0.5 rounded`}>-{dropoff}%</span>
                )}
              </div>
            </div>
            <div className="h-7 bg-slate-100 rounded-lg overflow-hidden relative">
              <div
                className={`h-full ${c.bar} rounded-lg transition-all duration-500`}
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BreakdownCard = ({ title, tooltip, data, total, color }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">
    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">{title} <MetricInfo text={tooltip} /></h4>
    <div className="space-y-2.5">
      {(data || []).filter(d => d.name && d.name !== 'null').map((d, i) => {
        const pct = Math.round((d.count / (total || 1)) * 100);
        return (
          <div key={`${d.name}-${i}`}>
            <div className="flex justify-between text-sm mb-1">
              <span className="capitalize text-slate-600">{d.name}</span>
              <span className="text-slate-500">{d.count} ({pct}%)</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default MetricsTab;
